import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { getActiveEvent, getBillettyper, FALLBACK_BILLETTYPER } from '../lib/cms';

export default function Billetter() {
  const [eventData, setEventData] = useState(null);
  const [billettyper, setBillettyper] = useState(FALLBACK_BILLETTYPER);

  useEffect(() => {
    getActiveEvent().then(setEventData);
    getBillettyper().then(data => {
      if (data && data.length > 0) {
        setBillettyper(data);
      }
    });
  }, []);

  const billetlink = eventData?.billetlink || "https://billet.eventbilletten.dk/seller/foreningen-rbike-zd1k";
  const billetstatus = eventData?.billetstatus || "Billetter til salg";

  // Find specifikke billetter fra CMS hvis tilgængelige
  const priorityTicket = billettyper.find(b => b.titel.toLowerCase().includes('priority')) || billettyper[0];
  const essentialTicket = billettyper.find(b => b.titel.toLowerCase().includes('essential') || b.titel.toLowerCase().includes('standard')) || billettyper[1];
  const morgenmadTicket = billettyper.find(b => b.titel.toLowerCase().includes('morgenmad'));
  const frokostTicket = billettyper.find(b => b.titel.toLowerCase().includes('frokost'));
  const aftensmadTicket = billettyper.find(b => b.titel.toLowerCase().includes('aftensmad'));
  const fuldPakkeTicket = billettyper.find(b => b.titel.toLowerCase().includes('fuld'));

  return (
    <div className="bg-[#090a0f] text-slate-100 min-h-screen pb-20">
      <Helmet>
        <title>Billetter - Foreningen ØrBike</title>
        <meta
          name="description"
          content="Køb billetter til ØrBike 2026 via Eventbilletten. Vælg mellem Priority cykler, standard cykler og forplejning for hele dagen i Ørbæk Midtpunkt."
        />
        <meta property="og:title" content="Billetter - Foreningen ØrBike" />
        <meta property="og:description" content="Se priskategorier for cykler og forplejningspakker til Danmarks største bike fest." />
      </Helmet>

      {/* Hero Header */}
      <section className="relative pt-36 sm:pt-40 pb-16 sm:pb-20 bg-gradient-to-b from-[#161829] to-[#090a0f] border-b border-pink-500/20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-pink-500/20 text-pink-400 border border-pink-500/30 mb-4">
            Eksternt billetsalg via Eventbilletten
          </span>
          <h1 className="text-4xl sm:text-6xl font-black font-display text-white uppercase tracking-tight">
            Billetter til ØrBike
          </h1>
          <p className="text-slate-300 text-sm sm:text-lg mt-3 max-w-2xl mx-auto leading-relaxed">
            Vi glæder os allerede til næste gang: <strong>ØrBike vender tilbage lørdag den 14. november 2026</strong>.
          </p>
          <div className="mt-4 p-4 rounded-xl bg-pink-950/40 border border-pink-500/40 max-w-xl mx-auto text-xs sm:text-sm text-pink-200">
            <i className="fa-solid fa-circle-info mr-2 text-pink-400" aria-hidden="true"></i>
            Du køber både <strong>eventbilletter</strong> og <strong>forplejningsbilletter</strong> sikkert og nemt via Eventbilletten.
          </div>
        </div>
      </section>

      {/* Cykelkategorier */}
      <section className="py-16 max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs uppercase tracking-widest font-bold text-pink-400 font-mono">
            Vælg cykelkategori
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-white font-display uppercase tracking-tight mt-1">
            Cykler til ØrBike 2026
          </h2>
          <p className="text-slate-300 text-sm mt-2">
            ØrBike samler hvert år <strong>over 230 cykler</strong> – og vi glæder os til at fortsætte succesen i 2026.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Priskategori 1 - Priority */}
          <div className="bg-gradient-to-b from-[#181a29] to-[#0d0f1a] border-2 border-pink-500 rounded-3xl p-8 shadow-[0_0_35px_rgba(255,42,141,0.25)] flex flex-col justify-between relative">
            <div className="absolute top-4 right-4 bg-pink-500 text-white text-[11px] font-extrabold uppercase px-3.5 py-1 rounded-full tracking-wider shadow">
              Firma & Team Favorit
            </div>
            <div>
              <span className="text-xs font-mono font-bold text-pink-400 uppercase">Priskategori 1</span>
              <h3 className="text-3xl font-black font-display text-white uppercase mt-1">Priority Cykel</h3>
              <p className="text-xs text-slate-400 mt-1">Ideel profilering og team-oplevelse for virksomheder og større grupper</p>

              <div className="my-6">
                <div className="text-5xl font-black font-display text-white">{priorityTicket?.pris || "5.550 kr."}</div>
                <div className="text-xs text-slate-400 mt-1">{priorityTicket?.pris_detalje || "inkl. moms pr. cykel - ekskl. ordregebyr"}</div>
              </div>

              <ul className="space-y-3.5 text-sm text-slate-300">
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-pink-400 mt-1" aria-hidden="true"></i>
                  <span><strong>10 timers spinning</strong> i Danmarks mest energiske sal</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-pink-400 mt-1" aria-hidden="true"></i>
                  <span><strong>Placering på de 3 forreste rækker</strong> med optimalt udsyn til scenen</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-pink-400 mt-1" aria-hidden="true"></i>
                  <span><strong>Op til 10 deltagere pr. cykel</strong> (f.eks. én ny rytter for hver time)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-pink-400 mt-1" aria-hidden="true"></i>
                  <span><strong>Logo på storskærme</strong> i pauser samt i spisesalen</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-pink-400 mt-1" aria-hidden="true"></i>
                  <span>Mulighed for at vise <strong>20–30 sek. video</strong> i pauser og på SoMe</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-pink-400 mt-1" aria-hidden="true"></i>
                  <span>Logo på sponsor-/selfievæg og takkeopslag på Facebook</span>
                </li>
              </ul>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-800">
              <a
                href={billetlink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 py-4 px-6 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-sm uppercase tracking-wider shadow-lg hover:scale-105 transition-transform"
              >
                <i className="fa-solid fa-ticket" aria-hidden="true"></i>
                <span>Køb Priority Cykel på Eventbilletten</span>
              </a>
              <div className="mt-2 text-center">
                <Link to="/priority-cykler" className="text-xs text-pink-400 hover:underline">
                  Læs mere om firmasponsorat og fordele her →
                </Link>
              </div>
            </div>
          </div>

          {/* Priskategori 2 - Essential */}
          <div className="bg-[#12141f] border border-slate-800 hover:border-pink-500/40 rounded-3xl p-8 flex flex-col justify-between transition-colors">
            <div>
              <span className="text-xs font-mono font-bold text-purple-400 uppercase">Priskategori 2</span>
              <h3 className="text-3xl font-black font-display text-white uppercase mt-1">Essential Cykel</h3>
              <p className="text-xs text-slate-400 mt-1">For cykelvenner, private ryttere og mindre motionshold</p>

              <div className="my-6">
                <div className="text-5xl font-black font-display text-white">{essentialTicket?.pris || "1.275 kr."}</div>
                <div className="text-xs text-slate-400 mt-1">{essentialTicket?.pris_detalje || "inkl. moms pr. cykel - ekskl. ordregebyr"}</div>
              </div>

              <ul className="space-y-3.5 text-sm text-slate-300">
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-purple-400 mt-1" aria-hidden="true"></i>
                  <span><strong>10 timers spinning</strong> med Danmarks bedste instruktører</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-purple-400 mt-1" aria-hidden="true"></i>
                  <span><strong>Placering centralt for scenen</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-purple-400 mt-1" aria-hidden="true"></i>
                  <span><strong>Op til 5 personer pr. cykel</strong> (ekstra deltagere kan tilkøbes)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-purple-400 mt-1" aria-hidden="true"></i>
                  <span>Fuld adgang til alle faciliteter, omklædning og god stemning</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-purple-400 mt-1" aria-hidden="true"></i>
                  <span>Støtter automatisk et godt formål for børn med kritisk sygdom</span>
                </li>
              </ul>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-800">
              <a
                href={billetlink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 py-4 px-6 rounded-full bg-slate-800 hover:bg-pink-600 hover:border-pink-600 text-white font-bold text-sm uppercase tracking-wider transition-colors"
              >
                <i className="fa-solid fa-ticket" aria-hidden="true"></i>
                <span>Køb Essential Cykel på Eventbilletten</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Forplejningsbilletter */}
      <section className="py-16 bg-[#0c0e17] border-y border-pink-500/20">
        <div className="max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs uppercase tracking-widest font-bold text-purple-400 font-mono">
              Energi til hele dagen
            </span>
            <h2 className="text-3xl sm:text-5xl font-black text-white font-display uppercase tracking-tight mt-1">
              Forplejning 2026
            </h2>
            <p className="text-slate-300 text-sm mt-2">
              Forplejningsbilletter kan købes separat, så du og dit hold er godt tanket op gennem alle 10 timer.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Morgenmad */}
            <div className="p-6 rounded-2xl bg-[#12141f] border border-slate-800 hover:border-pink-500/40 transition-colors flex flex-col justify-between">
              <div>
                <span className="text-xs font-mono text-pink-400 font-semibold">Kl. 7.30 - 11.00</span>
                <h4 className="text-2xl font-bold font-display text-white uppercase mt-1">{morgenmadTicket?.titel || "Morgenmad"}</h4>
                <div className="text-3xl font-black font-display text-white my-3">{morgenmadTicket?.pris || "105 kr."}</div>
                <div className="text-xs text-slate-400 mb-4">{morgenmadTicket?.pris_detalje || "pr. person"}</div>
                <ul className="space-y-2 text-xs text-slate-300">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-pink-400" aria-hidden="true"></i>
                    <span>Morgenmadsbuffet m/brød, yoghurt mv.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-pink-400" aria-hidden="true"></i>
                    <span>Kaffe, The, Vand</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-pink-400" aria-hidden="true"></i>
                    <span>Hjemmelavet smoothie</span>
                  </li>
                </ul>
              </div>
              <a
                href={billetlink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 w-full py-2.5 rounded-xl bg-slate-800 hover:bg-pink-600 text-white font-bold text-xs uppercase text-center tracking-wider transition-colors"
              >
                Køb forplejning
              </a>
            </div>

            {/* Frokost */}
            <div className="p-6 rounded-2xl bg-[#12141f] border border-slate-800 hover:border-pink-500/40 transition-colors flex flex-col justify-between">
              <div>
                <span className="text-xs font-mono text-purple-400 font-semibold">Kl. 11.30 - 16.30</span>
                <h4 className="text-2xl font-bold font-display text-white uppercase mt-1">{frokostTicket?.titel || "Frokost"}</h4>
                <div className="text-3xl font-black font-display text-white my-3">{frokostTicket?.pris || "159 kr."}</div>
                <div className="text-xs text-slate-400 mb-4">{frokostTicket?.pris_detalje || "pr. person"}</div>
                <ul className="space-y-2 text-xs text-slate-300">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-purple-400" aria-hidden="true"></i>
                    <span>Frokostbuffet m/salater, brød, lunt</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-purple-400" aria-hidden="true"></i>
                    <span>Kaffe, The, Vand</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-purple-400" aria-hidden="true"></i>
                    <span>Hjemmebagt kage</span>
                  </li>
                </ul>
              </div>
              <a
                href={billetlink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 w-full py-2.5 rounded-xl bg-slate-800 hover:bg-purple-600 text-white font-bold text-xs uppercase text-center tracking-wider transition-colors"
              >
                Køb forplejning
              </a>
            </div>

            {/* Aftensmad */}
            <div className="p-6 rounded-2xl bg-[#12141f] border border-slate-800 hover:border-pink-500/40 transition-colors flex flex-col justify-between">
              <div>
                <span className="text-xs font-mono text-pink-400 font-semibold">Kl. 18.30 - 20.30</span>
                <h4 className="text-2xl font-bold font-display text-white uppercase mt-1">{aftensmadTicket?.titel || "Aftensmad"}</h4>
                <div className="text-3xl font-black font-display text-white my-3">{aftensmadTicket?.pris || "169 kr."}</div>
                <div className="text-xs text-slate-400 mb-4">{aftensmadTicket?.pris_detalje || "pr. person"}</div>
                <ul className="space-y-2 text-xs text-slate-300">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-pink-400" aria-hidden="true"></i>
                    <span>1 x burger i "take-away" boks</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-pink-400" aria-hidden="true"></i>
                    <span>Lækker dessert</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-pink-400" aria-hidden="true"></i>
                    <span>Øl og vand</span>
                  </li>
                </ul>
              </div>
              <a
                href={billetlink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 w-full py-2.5 rounded-xl bg-slate-800 hover:bg-pink-600 text-white font-bold text-xs uppercase text-center tracking-wider transition-colors"
              >
                Køb forplejning
              </a>
            </div>

            {/* Fuld Forplejning */}
            <div className="p-6 rounded-2xl bg-gradient-to-b from-purple-950/40 to-slate-900 border border-purple-500/40 flex flex-col justify-between shadow-[0_0_25px_rgba(168,85,247,0.2)]">
              <div>
                <span className="text-xs font-mono text-purple-300 font-bold uppercase">Hele dagen</span>
                <h4 className="text-2xl font-bold font-display text-white uppercase mt-1">{fuldPakkeTicket?.titel || "Fuld pakke"}</h4>
                <div className="text-3xl font-black font-display text-white my-3">{fuldPakkeTicket?.pris || "399 kr."}</div>
                <div className="text-xs text-slate-400 mb-4">{fuldPakkeTicket?.pris_detalje || "pr. person"}</div>
                <ul className="space-y-2 text-xs text-slate-200">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-star text-pink-400" aria-hidden="true"></i>
                    <span>Morgenmadsbuffet</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-star text-pink-400" aria-hidden="true"></i>
                    <span>Frokostbuffet m/kaffe & kage</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-star text-pink-400" aria-hidden="true"></i>
                    <span>Aftensmad m/dessert, øl eller vand</span>
                  </li>
                </ul>
              </div>
              <a
                href={billetlink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 w-full py-2.5 rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-xs uppercase text-center tracking-wider shadow hover:scale-105 transition-transform"
              >
                Køb fuld forplejning
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Praktiske Billetnoter */}
      <section className="py-16 max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
        <h3 className="text-2xl sm:text-3xl font-bold font-display text-white uppercase">
          Vigtigt at vide om billetkøb
        </h3>
        <p className="text-slate-300 text-sm leading-relaxed">
          Alle billetter udstedes direkte via <strong>Eventbilletten</strong>. Ved køb modtager du din ordrebekræftelse og billet direkte på din e-mail. Ved ankomst til Ørbæk Midtpunkt skal du blot fremvise din billet i check-in området ved indgangen.
        </p>
        <div className="pt-2">
          <Link
            to="/praktisk-info"
            className="text-pink-400 hover:text-pink-300 underline font-semibold text-sm"
          >
            Læs alt om parkering, pedaler, klamper og check-in på Praktisk info →
          </Link>
        </div>
      </section>
    </div>
  );
}
